import { Button } from '@app/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@app/components/ui/tooltip';
import { cn } from '@app/lib/utils';
import type { ResolvedTheme, ThemePreference } from '@cloud-ui/hooks/useThemePreference';

import { Monitor, Moon, Sun } from 'lucide-react';

const THEME_ORDER = ['light', 'system', 'dark'] as const satisfies readonly ThemePreference[];

const NEXT_THEME_PREFERENCE = {
  dark: 'light',
  light: 'system',
  system: 'dark',
} satisfies Record<ThemePreference, ThemePreference>;

const THEME_OPTIONS = {
  dark: {
    icon: Moon,
    label: 'Dark theme',
  },
  light: {
    icon: Sun,
    label: 'Light theme',
  },
  system: {
    icon: Monitor,
    label: 'System theme',
  },
} satisfies Record<
  ThemePreference,
  {
    icon: typeof Moon;
    label: string;
  }
>;

interface ThemeSelectorProps {
  themePreference: ThemePreference;
  systemTheme: ResolvedTheme;
  onThemePreferenceChange: (preference: ThemePreference) => void;
}

export default function ThemeSelector({
  themePreference,
  systemTheme,
  onThemePreferenceChange,
}: ThemeSelectorProps) {
  const nextThemePreference = NEXT_THEME_PREFERENCE[themePreference];
  const currentThemeLabel = THEME_OPTIONS[themePreference].label;
  const nextThemeLabel = THEME_OPTIONS[nextThemePreference].label;
  const currentStateLabel =
    themePreference === 'system' ? `${currentThemeLabel} (${systemTheme})` : currentThemeLabel;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => onThemePreferenceChange(nextThemePreference)}
          aria-label={`Theme preference: ${currentStateLabel}. Switch to ${nextThemeLabel}.`}
          className="relative rounded-full transition-transform hover:rotate-[15deg]"
        >
          {THEME_ORDER.map((preference) => {
            const Icon = THEME_OPTIONS[preference].icon;
            const isActive = preference === themePreference;

            return (
              <span
                key={preference}
                className={cn(
                  'absolute flex transition-all',
                  isActive ? 'rotate-0 opacity-100' : 'rotate-90 opacity-0',
                )}
              >
                <Icon
                  aria-hidden="true"
                  className="size-4"
                  data-testid={`${preference}-theme-icon`}
                />
              </span>
            );
          })}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{currentStateLabel}</TooltipContent>
    </Tooltip>
  );
}
